import { Loader2, Pause, Play } from "lucide-react";
import type { MouseEvent } from "react";
import type { AutoCombineEntryV2 } from "@/shared/cache";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { useSmartTimer } from "@/v2/hooks/useSmartTimer";
import { cn } from "@/v2/lib/utils";

// Only tracked/reviewed groups can receive timer work; exported/deleted are inert.
const ELIGIBLE_STATUSES = new Set<AutoCombineEntryV2["status"]>([
	"tracked",
	"reviewed",
]);

/**
 * The per-row smart-timer control, rendered in the table's trailing `timer`
 * column. Reads the single running timer from context so every row stays in
 * sync (including optimistic start/switch/pause) without threading props
 * through the memoized row. Ineligible rows render nothing to preserve density.
 */
export function RowTimerControl({ entry }: { entry: AutoCombineEntryV2 }) {
	const {
		activeEntryGroupId,
		pendingTargetId,
		pausingEntryGroupId,
		isBusy,
		isSavingEntries,
		startOrSwitch,
		pause,
	} = useSmartTimer();

	if (!ELIGIBLE_STATUSES.has(entry.status)) return null;

	const isActive = activeEntryGroupId === entry.id;
	const isSpinner =
		pendingTargetId === entry.id || pausingEntryGroupId === entry.id;
	const timerRunsElsewhere = activeEntryGroupId !== null && !isActive;
	const visible = isActive || isSpinner;
	const isDisabled = isBusy || (!isActive && isSavingEntries);

	const label = isActive
		? "Pause"
		: timerRunsElsewhere
			? "Switch timer to this group"
			: "Start timer for this group";
	const tooltip = isActive
		? "Pause"
		: timerRunsElsewhere
			? "Switch timer here"
			: "Start timer";

	const handleClick = (event: MouseEvent) => {
		event.stopPropagation();
		if (isDisabled) return;
		if (isActive) pause();
		else startOrSwitch(entry.id);
	};

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					aria-label={label}
					aria-pressed={isActive}
					disabled={isDisabled && !isSpinner}
					onClick={handleClick}
					onMouseDown={(event) => event.stopPropagation()}
					className={cn(
						"relative flex size-7 items-center justify-center rounded-md transition-colors",
						"focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
						isActive
							? "bg-ring text-background"
							: cn(
									"text-muted-foreground hover:bg-muted hover:text-foreground",
									timerRunsElsewhere && "text-ring",
								),
						visible ? "opacity-100" : "opacity-0 group-hover/row:opacity-100",
						isDisabled && !isSpinner && "cursor-default opacity-40",
					)}
				>
					{isSpinner ? (
						<Loader2 className="size-3.5 animate-spin" />
					) : isActive ? (
						<Pause className="size-3.5" />
					) : (
						<Play className="size-3.5 fill-current" />
					)}
				</button>
			</TooltipTrigger>
			<TooltipContent side="left">{tooltip}</TooltipContent>
		</Tooltip>
	);
}
